// ProjectDetailPage — one project's workspace: header (folder + inline-rename name + description) over
// two equal-width panels. CHATS (left): the project's member chats — searchable, each row opens the
// chat; add/remove acts here. ARTIFACTS (right): the artifacts those chats produced — searchable,
// read-only/derived (an artifact belongs to its chat; project membership is inherited, so there's no
// per-artifact add/remove). Each panel scrolls within itself under a sticky header. Studio tokens.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar from '@mui/material/Snackbar'
import Tooltip from '@mui/material/Tooltip'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ChatIcon from '@mui/icons-material/Chat'
import WidgetsIcon from '@mui/icons-material/Widgets'
import AddIcon from '@mui/icons-material/Add'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { useRosShell } from '../RosShellContext'
import {
  getProject, updateProject, addChatToProject, removeChatFromProject, listProjects,
  type ProjectDetail, type ProjectChat, type ProjectListItem,
} from './projectsApi'
import { typeDef, TONE_COLOR, retentionFor, TIER_COLOR, relativeModified, latestVersion, type RetentionState } from './artifactsModel'
import { ArtifactIndicator } from '../components/ArtifactIndicator'
import type { SessionArtifact } from '../components/rosArtifacts'
import type { SessionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import { SERIF_HEADING, SERIF_HEADING_CSS } from '../theme'

// Same relative-time semantics as the Chats list page, so "Last active" reads identically across tabs.
function relativeTime(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86_400_000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 14) return 'Last week'
  if (days < 31) return `${Math.floor(days / 7)} weeks ago`
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// A chat's secondary line — only shown when we actually have metadata (panel type). Never fabricated.
const PANEL_LABEL: Record<string, string> = { crosstabs: 'Crosstab', survey_review: 'Survey', site_builder: 'Website' }
function chatSubtitle(c: ProjectChat): string | null {
  return c.panelType ? (PANEL_LABEL[c.panelType] ?? null) : null
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const shell = useRosShell()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<ProjectDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [renaming, setRenaming] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const [editingDesc, setEditingDesc] = useState(false)
  const [descValue, setDescValue] = useState('')
  const [addOpen, setAddOpen] = useState<null | HTMLElement>(null)
  const [chatPickerQuery, setChatPickerQuery] = useState('')
  const [moveCandidate, setMoveCandidate] = useState<SessionSummary | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  // Per-chat ⋮ menu + its "Move to project…" submenu (loaded lazily), mirroring the Chats page.
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; chat: ProjectChat } | null>(null)
  const [moveMenu, setMoveMenu] = useState<{ anchor: HTMLElement; chat: ProjectChat } | null>(null)
  const [otherProjects, setOtherProjects] = useState<ProjectListItem[]>([])

  const reload = useCallback(async () => {
    if (!projectId) return
    try { setDetail(await getProject(shell.apiBaseUrl, shell.token, projectId)) }
    catch { setDetail(null) }
    finally { setLoading(false) }
  }, [projectId, shell.apiBaseUrl, shell.token])

  useEffect(() => { void reload() }, [reload])

  // Chats NOT yet in this project — the add-chat picker source.
  const memberIds = useMemo(() => new Set((detail?.chats ?? []).map(c => c.id)), [detail])
  const addable = useMemo(
    () => shell.sessions.filter(s => !memberIds.has(s.id) && s.projectId !== projectId),
    [shell.sessions, memberIds, projectId],
  )
  const pickerChats = useMemo(() => {
    const query = chatPickerQuery.trim().toLowerCase()
    const matches = query
      ? addable.filter(session => `${session.intentSummary ?? ''} ${session.projectName ?? ''}`.toLowerCase().includes(query))
      : addable
    return matches.slice(0, 50)
  }, [addable, chatPickerQuery])
  const unassignedPickerChats = useMemo(() => pickerChats.filter(session => !session.projectId), [pickerChats])
  const assignedPickerChats = useMemo(() => pickerChats.filter(session => Boolean(session.projectId)), [pickerChats])

  // Rename is triggered by a plain icon button (no Menu), so the autoFocus field never receives a
  // spurious focus-steal blur — the first blur it gets is the genuine user click-away, which should
  // commit. (No suppress-blur guard here, unlike the menu-triggered renames elsewhere.)
  const startRename = () => { setNameValue(detail?.project.name ?? ''); setRenaming(true) }
  const commitRename = async () => {
    if (!renaming || !projectId) return
    const name = nameValue.trim()
    setRenaming(false)
    if (!name || name === detail?.project.name) return
    setDetail(d => d ? { ...d, project: { ...d.project, name } } : d)
    try { await updateProject(shell.apiBaseUrl, shell.token, projectId, { name }) } catch { void reload() }
  }

  // Description edit — same plain-field pattern as rename. Empty commits as null (clears it).
  const startDescEdit = () => { setDescValue(detail?.project.description ?? ''); setEditingDesc(true) }
  const commitDesc = async () => {
    if (!editingDesc || !projectId) return
    const desc = descValue.trim()
    setEditingDesc(false)
    if (desc === (detail?.project.description ?? '')) return
    setDetail(d => d ? { ...d, project: { ...d.project, description: desc || null } } : d)
    try { await updateProject(shell.apiBaseUrl, shell.token, projectId, { description: desc }) } catch { void reload() }
  }

  const assignChat = async (session: SessionSummary) => {
    setAddOpen(null)
    if (!projectId) return
    const previousProjectName = session.projectName
    try {
      await addChatToProject(shell.apiBaseUrl, shell.token, projectId, session.id)
      await reload(); shell.reloadSessions()
      setToast(previousProjectName
        ? `Chat moved from ${previousProjectName} to ${detail?.project.name ?? 'this project'}.`
        : `Chat added to ${detail?.project.name ?? 'this project'}.`)
    }
    catch { setToast(previousProjectName ? 'Could not move chat.' : 'Could not add chat.') }
  }
  const choosePickerChat = (session: SessionSummary) => {
    setAddOpen(null)
    if (session.projectId) setMoveCandidate(session)
    else void assignChat(session)
  }
  const confirmPickerMove = () => {
    const session = moveCandidate
    setMoveCandidate(null)
    if (session) void assignChat(session)
  }
  const removeChat = async (sessionId: string) => {
    setRowMenu(null)
    if (!projectId) return
    setDetail(d => d ? { ...d, chats: d.chats.filter(c => c.id !== sessionId) } : d)  // optimistic
    try {
      await removeChatFromProject(shell.apiBaseUrl, shell.token, projectId, sessionId)
      await reload(); shell.reloadSessions(); setToast('Removed from project.')
    }
    catch { void reload() }
  }

  // "Move to project…": open the submenu with the user's OTHER projects (loaded lazily). Anchor to the
  // ⋮ button, which stays mounted — the clicked menu item unmounts when the first menu closes.
  const openMoveMenu = async (anchor: HTMLElement, chat: ProjectChat) => {
    setRowMenu(null)
    setMoveMenu({ anchor, chat })
    try { setOtherProjects((await listProjects(shell.apiBaseUrl, shell.token)).filter(p => p.id !== projectId)) }
    catch { setOtherProjects([]) }
  }
  // The assignment endpoint is an atomic move; never issue a second remove that could erase the new
  // membership if requests complete out of order.
  const moveToProject = async (targetId: string) => {
    const chat = moveMenu?.chat
    setMoveMenu(null)
    if (!chat || !projectId) return
    setDetail(d => d ? { ...d, chats: d.chats.filter(c => c.id !== chat.id) } : d)  // optimistic
    try {
      await addChatToProject(shell.apiBaseUrl, shell.token, targetId, chat.id)
      await reload(); shell.reloadSessions(); setToast('Chat moved.')
    } catch { void reload(); setToast('Could not move chat.') }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>
  if (!detail) return (
    <Box sx={{ maxWidth: 1340, mx: 'auto', px: 4, py: 5 }}>
      <Button startIcon={<ChevronLeftIcon />} onClick={() => navigate('/projects')} sx={{ textTransform: 'none' }}>All projects</Button>
      <Typography sx={{ mt: 2, color: 'text.secondary' }}>This project couldn't be found.</Typography>
    </Box>
  )

  return (
    <Box sx={{ maxWidth: 1340, mx: 'auto', px: 4, py: 4, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Button startIcon={<ChevronLeftIcon />} onClick={() => navigate('/projects')} sx={{ textTransform: 'none', alignSelf: 'flex-start', mb: 1.5, color: 'primary.main', fontWeight: 600 }}>All projects</Button>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3.5, flexShrink: 0 }}>
        <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,143,168,0.10)', color: 'primary.main' }}><FolderIcon sx={{ fontSize: 28 }} /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {renaming ? (
            <TextField
              autoFocus size="small" value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onBlur={() => void commitRename()}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void commitRename() } else if (e.key === 'Escape') setRenaming(false) }}
              sx={{ minWidth: 420, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '1.8rem', ...SERIF_HEADING_CSS } }}
            />
          ) : (
            <Typography variant="h3" sx={{ ...SERIF_HEADING, display: 'flex', alignItems: 'center', gap: 1 }}>
              {detail.project.name}
              <IconButton size="small" onClick={startRename} sx={{ color: 'text.disabled' }}><EditOutlinedIcon fontSize="small" /></IconButton>
            </Typography>
          )}
          {editingDesc ? (
            <TextField
              autoFocus fullWidth size="small" value={descValue} placeholder="Add a short description…"
              onChange={e => setDescValue(e.target.value)}
              onBlur={() => void commitDesc()}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void commitDesc() } else if (e.key === 'Escape') setEditingDesc(false) }}
              sx={{ mt: 0.75, maxWidth: 640, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.9rem' } }}
            />
          ) : detail.project.description ? (
            <Typography
              onClick={startDescEdit}
              sx={{ color: 'text.secondary', mt: 0.5, cursor: 'text', borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': { bgcolor: 'action.hover' } }}
              title="Click to edit description"
            >{detail.project.description}</Typography>
          ) : (
            <Typography
              onClick={startDescEdit}
              sx={{ color: 'text.disabled', mt: 0.5, cursor: 'text', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': { bgcolor: 'action.hover', color: 'text.secondary' } }}
            ><EditOutlinedIcon sx={{ fontSize: 16 }} /> Add a description</Typography>
          )}
        </Box>
      </Box>

      {/* Two equal panels, each scrolls within itself. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, flex: 1, minHeight: 0 }}>
        <ChatsPanel
          chats={detail.chats}
          artifactCountBySession={shell.artifactCountBySession}
          onOpen={id => shell.selectSession(id)}
          onRowMenu={(e, chat) => setRowMenu({ anchor: e.currentTarget, chat })}
          onAdd={e => { setChatPickerQuery(''); setAddOpen(e.currentTarget) }}
          onNewChat={() => { if (projectId) shell.newChatInProject(projectId) }}
          canAdd={addable.length > 0}
        />
        <ArtifactsPanel artifacts={detail.artifacts} onOpenInChat={artifact => { void shell.openArtifactInChat(artifact) }} />
      </Box>

      {/* Per-chat ⋮ menu — matches the Chats page styling (Open / Move / Remove). Hard-deleting the
          underlying chat is intentionally NOT here — that lives on the Chats page; from a project you
          only manage membership, so a chat is never lost by acting inside a project. */}
      <Menu anchorEl={rowMenu?.anchor} open={!!rowMenu} onClose={() => setRowMenu(null)} disableRestoreFocus anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { if (rowMenu) { shell.selectSession(rowMenu.chat.id); setRowMenu(null) } }}>
          <ListItemIcon><LaunchOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open chat</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (rowMenu) void openMoveMenu(rowMenu.anchor, rowMenu.chat) }}>
          <ListItemIcon><DriveFileMoveOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Move to project…</ListItemText>
          <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', ml: 1.5 }} />
        </MenuItem>
        <MenuItem onClick={() => { if (rowMenu) void removeChat(rowMenu.chat.id) }}>
          <ListItemIcon><RemoveCircleOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Remove from project</ListItemText>
        </MenuItem>
      </Menu>

      {/* "Move to project" submenu — the user's OTHER projects. */}
      <Menu anchorEl={moveMenu?.anchor} open={!!moveMenu} onClose={() => setMoveMenu(null)} PaperProps={{ sx: { maxHeight: 320, minWidth: 220 } }} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {otherProjects.length === 0
          ? <MenuItem disabled>No other projects</MenuItem>
          : otherProjects.map(p => (
            <MenuItem key={p.id} onClick={() => void moveToProject(p.id)}>
              <ListItemIcon><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.85rem' } }}>{p.name}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Add/move picker — current ownership is visible before the user acts. Unassigned chats add
          immediately; selecting a chat from another project asks for confirmation below. */}
      <Menu
        anchorEl={addOpen} open={!!addOpen} onClose={() => setAddOpen(null)}
        PaperProps={{ sx: { maxHeight: 480, width: 420, borderRadius: 2.5 } }}
        MenuListProps={{ autoFocusItem: false, sx: { py: 0.5 } }}
      >
        <ListSubheader sx={{ bgcolor: 'background.paper', px: 1.5, pt: 1, pb: 1, lineHeight: 'normal' }}>
          <TextField
            autoFocus fullWidth size="small" placeholder="Search chats"
            value={chatPickerQuery} onChange={event => setChatPickerQuery(event.target.value)}
            onKeyDown={event => event.stopPropagation()}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </ListSubheader>

        {unassignedPickerChats.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: 1.8, pt: 0.75, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary' }}>
            Unassigned
          </ListSubheader>
        )}
        {unassignedPickerChats.map(session => (
          <MenuItem key={session.id} onClick={() => choosePickerChat(session)} sx={{ gap: 1, py: 1 }}>
            <ChatIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <ListItemText
              primary={session.intentSummary || 'Untitled chat'}
              primaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.86rem', fontWeight: 500 } }}
            />
            <ArtifactIndicator count={shell.artifactCountBySession[session.id]} />
            <Typography sx={{ ml: 0.5, fontSize: '0.72rem', fontWeight: 700, color: 'primary.main' }}>Add</Typography>
          </MenuItem>
        ))}

        {unassignedPickerChats.length > 0 && assignedPickerChats.length > 0 && <Divider />}
        {assignedPickerChats.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: 1.8, pt: 0.75, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary' }}>
            In other projects
          </ListSubheader>
        )}
        {assignedPickerChats.map(session => (
          <MenuItem key={session.id} onClick={() => choosePickerChat(session)} sx={{ gap: 1, py: 1 }}>
            <ChatIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <ListItemText
              primary={session.intentSummary || 'Untitled chat'}
              secondary={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, maxWidth: '100%' }}>
                  <FolderOutlinedIcon sx={{ fontSize: 14 }} />
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {session.projectName || 'Another project'}
                  </Box>
                </Box>
              }
              primaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.86rem', fontWeight: 500 } }}
              secondaryTypographyProps={{ component: 'div', sx: { mt: 0.25, fontSize: '0.74rem', color: 'text.secondary' } }}
            />
            <ArtifactIndicator count={shell.artifactCountBySession[session.id]} />
            <Typography sx={{ ml: 0.5, fontSize: '0.72rem', fontWeight: 700, color: 'primary.main' }}>Move</Typography>
          </MenuItem>
        ))}

        {pickerChats.length === 0 && (
          <MenuItem disabled sx={{ py: 2 }}>
            {chatPickerQuery ? 'No chats match your search' : 'All your chats are already here'}
          </MenuItem>
        )}
      </Menu>

      <Dialog open={Boolean(moveCandidate)} onClose={() => setMoveCandidate(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={SERIF_HEADING}>Move chat to {detail.project.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Move “{moveCandidate?.intentSummary || 'Untitled chat'}” from {moveCandidate?.projectName || 'its current project'} to {detail.project.name}? Its artifacts will move with the chat.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveCandidate(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmPickerMove}>Move chat</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast ?? ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  )
}

// Panel scaffold shared by both columns: outlined card, title row + right-aligned actions, search,
// then a column-header row (uppercase captions) over a scrolling body — the same visual language as
// the Chats and Artifacts list pages.
function PanelShell({ icon, title, count, actions, search, columns, gridCols, children }: {
  icon: React.ReactNode; title: string; count: number; actions?: React.ReactNode
  search: React.ReactNode; columns: string[]; gridCols: string; children: React.ReactNode
}) {
  return (
    // Header + count + actions and the search row sit ABOVE the table card — same structure as the
    // Chats list page. The table itself is an outlined Paper with the identical header-row tokens.
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, minHeight: 34 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {icon} {title}
          <Box component="span" sx={{ ml: 0.5, px: 0.9, py: 0.1, borderRadius: 999, bgcolor: 'action.hover', color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.6 }}>{count}</Box>
        </Typography>
        {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>{actions}</Box>}
      </Box>
      <Box sx={{ mb: 1.5 }}>{search}</Box>
      <Paper variant="outlined" sx={{ borderRadius: 2.5, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', flex: 1 }}>
        {/* Column header — identical tokens to the Chats/Artifacts list pages. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', px: 2.5, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          {columns.map((h, i) => (
            <Typography key={i} sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>{h}</Typography>
          ))}
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>{children}</Box>
      </Paper>
    </Box>
  )
}

const CHAT_GRID = '1fr 120px 40px'

// ── Chats panel — table style matching the Chats list page (CHAT / LAST ACTIVE / ⋮). ─────────────────
function ChatsPanel({ chats, artifactCountBySession, onOpen, onRowMenu, onAdd, onNewChat, canAdd }: {
  chats: ProjectChat[]
  artifactCountBySession: Record<string, number>
  onOpen: (id: string) => void
  onRowMenu: (e: React.MouseEvent<HTMLElement>, chat: ProjectChat) => void
  onAdd: (e: React.MouseEvent<HTMLElement>) => void; onNewChat: () => void; canAdd: boolean
}) {
  const [q, setQ] = useState('')
  const visible = useMemo(() => {
    const s = q.trim().toLowerCase()
    return chats.filter(c => (c.intentSummary || '').toLowerCase().includes(s))
  }, [chats, q])

  return (
    <PanelShell
      icon={<ChatIcon fontSize="small" sx={{ color: 'text.primary' }} />}
      title="Chats" count={chats.length}
      gridCols={CHAT_GRID} columns={['Chat', 'Last active', '']}
      actions={<>
        <Button size="small" startIcon={<AddIcon />} onClick={onAdd} disabled={!canAdd} sx={{ textTransform: 'none' }}>Add or move</Button>
        <Button size="small" variant="contained" startIcon={<AddCommentOutlinedIcon />} onClick={onNewChat} sx={{ textTransform: 'none' }}>New chat</Button>
      </>}
      search={
        <TextField fullWidth size="small" placeholder="Search chats" value={q} onChange={e => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      }
    >
      {visible.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600 }}>{chats.length === 0 ? 'No chats in this project' : 'No chats match'}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>{chats.length === 0 ? 'Start a new chat or add an existing one.' : 'Try a different search.'}</Typography>
        </Box>
      ) : visible.map((c, i) => {
        const subtitle = chatSubtitle(c)
        return (
          <Box key={c.id} onClick={() => onOpen(c.id)}
            sx={{ display: 'grid', gridTemplateColumns: CHAT_GRID, alignItems: 'center', px: 2.5, py: 1.4, cursor: 'pointer', borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, '&:hover .row-more': { opacity: 1 } }}>
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{c.intentSummary || 'Untitled chat'}</Typography>
                <ArtifactIndicator count={artifactCountBySession[c.id]} />
              </Box>
              {subtitle && <Typography noWrap variant="caption" sx={{ color: 'text.disabled' }}>{subtitle}</Typography>}
            </Box>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{relativeTime(c.lastActiveAt ?? c.startedAt)}</Typography>
            <Box sx={{ justifySelf: 'end' }}>
              <IconButton className="row-more" size="small" onClick={e => { e.stopPropagation(); onRowMenu(e, c) }} sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s', color: 'text.secondary' }}><MoreVertIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        )
      })}
    </PanelShell>
  )
}

const ART_GRID = '1fr 90px 110px 120px'

// ── Artifacts panel — table style matching the Artifacts list page (NAME / TYPE / MODIFIED / RETENTION). ──
function ArtifactsPanel({ artifacts, onOpenInChat }: { artifacts: SessionArtifact[]; onOpenInChat: (artifact: SessionArtifact) => void }) {
  const [q, setQ] = useState('')
  const visible = useMemo(() => {
    const s = q.trim().toLowerCase()
    return artifacts.filter(a => a.title.toLowerCase().includes(s))
  }, [artifacts, q])

  return (
    <PanelShell
      icon={<WidgetsIcon fontSize="small" sx={{ color: 'text.primary' }} />}
      title="Artifacts" count={artifacts.length}
      gridCols={ART_GRID} columns={['Name', 'Type', 'Modified', 'Retention']}
      search={
        <TextField fullWidth size="small" placeholder="Search artifacts" value={q} onChange={e => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      }
    >
      {visible.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600 }}>{artifacts.length === 0 ? 'No artifacts in this project' : 'No artifacts match'}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>{artifacts.length === 0 ? 'Artifacts Ros makes in these chats appear here.' : 'Try a different search.'}</Typography>
        </Box>
      ) : visible.map((a, i) => {
        const def = typeDef(a.type)
        const color = TONE_COLOR[def.tone]
        const lv = latestVersion(a)
        const r = retentionFor(lv)
        return (
          <Box key={a.id} onClick={() => onOpenInChat(a)}
            sx={{ display: 'grid', gridTemplateColumns: ART_GRID, alignItems: 'center', px: 2.5, py: 1.4, cursor: 'pointer', borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, pr: 1.5 }}>
              <Box sx={{ width: 30, height: 30, flexShrink: 0, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}14`, color }}><WidgetsIcon sx={{ fontSize: 16 }} /></Box>
              <Typography noWrap sx={{ fontSize: '0.88rem', fontWeight: 500 }}>{a.title}</Typography>
            </Box>
            <Box><Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, border: '1px solid', borderColor: `${color}55`, color, bgcolor: `${color}0F`, fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{def.label}</Box></Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(lv.publishedAt)}</Typography>
            <RetentionChip r={r} />
          </Box>
        )
      })}
    </PanelShell>
  )
}

// Small retention pill, matching the Artifacts list page's tiered color + icon.
function RetentionChip({ r }: { r: RetentionState }) {
  const color = TIER_COLOR[r.tier]
  return (
    <Tooltip title={`Expires ${new Date(r.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color, fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap' }}>{r.label}</Box>
    </Tooltip>
  )
}
